/**
 * Returns a Map-like object with LRU eviction. Insertion order is preserved by `Map`,
 * so re-inserting on `set` moves the entry to the most-recent slot; the oldest slot
 * (first key from iteration) is evicted when size exceeds `maxSize`.
 */
export function createLruMap<K, V>(
  maxSize: number
): {
  get: (key: K) => V | undefined;
  set: (key: K, value: V) => void;
  has: (key: K) => boolean;
  delete: (key: K) => boolean;
  clear: () => void;
  keys: () => IterableIterator<K>;
  readonly size: number;
} {
  const map = new Map<K, V>();
  return {
    get: (key) => map.get(key),
    set: (key, value) => {
      map.delete(key);
      map.set(key, value);
      if (map.size > maxSize) {
        const oldestKey = map.keys().next().value;
        if (oldestKey !== undefined) {
          map.delete(oldestKey);
        }
      }
    },
    has: (key) => map.has(key),
    delete: (key) => map.delete(key),
    clear: () => map.clear(),
    keys: () => map.keys(),
    get size() {
      return map.size;
    },
  };
}
